import { MINUTE_IN_MS } from '@/lib/constants/time';
import { AuthenticatedRoutes } from '@/router/routes';

const CONNECT_AI_NONCE_KEY = 'connect_ai_nonce';
// Matches the lifetime of the authorization request on the backend.
const CONNECT_AI_NONCE_TTL_MS = 10 * MINUTE_IN_MS;

interface StoredNonce {
  nonce: string;
  timestamp: number;
}

/**
 * Keeps the `nonce` of /connect-ai across the login redirect: the router sends a signed-out user to /login
 * and later back to the bare /connect-ai path, dropping the query string.
 * Must run before the router redirects, i.e. on app start.
 */
export const rememberConnectAiNonce = (location: Pick<Location, 'pathname' | 'search'>): void => {
  if (location.pathname !== AuthenticatedRoutes.ConnectAi) {
    return;
  }
  const nonce = new URLSearchParams(location.search).get('nonce');
  if (!nonce) {
    return;
  }
  const stored: StoredNonce = { nonce, timestamp: Date.now() };
  sessionStorage.setItem(CONNECT_AI_NONCE_KEY, JSON.stringify(stored));
};

/**
 * Returns the remembered nonce once and forgets it, or null when there is none or it has expired.
 */
export const consumeConnectAiNonce = (): string | null => {
  const storedJson = sessionStorage.getItem(CONNECT_AI_NONCE_KEY);
  sessionStorage.removeItem(CONNECT_AI_NONCE_KEY);
  if (!storedJson) {
    return null;
  }
  try {
    const stored: StoredNonce = JSON.parse(storedJson);
    return Date.now() - stored.timestamp > CONNECT_AI_NONCE_TTL_MS ? null : stored.nonce;
  } catch {
    return null;
  }
};
